import React from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
    Alert,
} from '@mui/material';
import {
    ArrowBack,
    Refresh,
    AutoAwesome,
    AccessTime,
} from '@mui/icons-material';
import { ComparisonCandidate, TestResult } from './types';
import { CandidateAccordion } from './components/CandidateAccordion';

interface ComparisonViewProps {
    candidates: ComparisonCandidate[];
    newResults: TestResult[];
    oldResults?: TestResult[];
    compareWithActive: boolean;
    parentVersion?: string;
    timestamp: string;
    onBack: () => void;
    onReEvaluate: () => void;
    onApplyToAll: () => void;
    evaluationExperiment?: boolean | null;
}

export const ComparisonView: React.FC<ComparisonViewProps> = ({
    candidates,
    newResults,
    oldResults = [],
    compareWithActive,
    parentVersion,
    timestamp,
    onBack,
    onReEvaluate,
    onApplyToAll,
    evaluationExperiment,
}) => {
    const [expanded, setExpanded] = React.useState<string[]>(candidates.map(c => c.id));

    const handleAccordionChange = (candidateId: string) => {
        setExpanded(prev =>
            prev.includes(candidateId)
                ? prev.filter(id => id !== candidateId)
                : [...prev, candidateId]
        );
    };

    const getScoreDiff = (candidateId: string): number => {
        const newResult = newResults.find(r => r.candidateId === candidateId);
        const oldResult = oldResults.find(r => r.candidateId === candidateId);
        if (!newResult || !oldResult) return 0;
        return Math.round((newResult.score - oldResult.score) * 100);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Timestamp Alert */}
            <Alert
                icon={<AccessTime />}
                severity="info"
                sx={{ mb: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}
                action={
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={onReEvaluate}
                        sx={{ textTransform: 'none' }}
                    >
                        Re-evaluate
                    </Button>
                }
            >
                Showing results from {timestamp}
            </Alert>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Button
                    variant="text"
                    startIcon={<ArrowBack />}
                    onClick={onBack}
                    sx={{ textTransform: 'none' }}
                >
                    Back to Selection
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<AutoAwesome />}
                        onClick={onApplyToAll}
                        sx={{ textTransform: 'none' }}
                    >
                        Apply to All Candidates
                    </Button>
                </Box>
            </Box>

            {/* Comparison Header */}
            {compareWithActive && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={evaluationExperiment === true ? "Current Active" : "Parent"} variant="outlined" size="small" />
                                <Typography variant="body2" color="text.secondary">
                                    {parentVersion}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                    <Card sx={{ bgcolor: 'rgba(25, 118, 210, 0.05)', border: '1px solid rgba(25, 118, 210, 0.5)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label="New" color="primary" size="small" />
                                <Typography variant="body2" color="text.secondary">
                                    Draft
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Results Accordion */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {candidates.map((candidate) => {
                    const newResult = newResults.find(r => r.candidateId === candidate.id);
                    const oldResult = oldResults.find(r => r.candidateId === candidate.id);
                    const scoreDiff = getScoreDiff(candidate.id);

                    return (
                        <CandidateAccordion
                            key={candidate.id}
                            candidate={candidate}
                            expanded={expanded.includes(candidate.id)}
                            onChange={() => handleAccordionChange(candidate.id)}
                            newResult={newResult}
                            oldResult={oldResult}
                            compareWithActive={compareWithActive}
                            scoreDiff={scoreDiff}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};
